import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { C } from "../lib/theme";
import { Card } from "../components/Card";
import { Icon } from "../components/Icon";
import { Skeleton } from "../components/Skeleton";
import { EmptyState } from "../components/EmptyState";
import { supabase } from "../lib/supabase";
import { logChannelStatus } from "../lib/realtimeStatus";
import { parseTs } from "../lib/dateUtils";
import { encodeOrderRef, decodeOrderRef, orderRefPreview } from "../lib/orderRef";

// Messages — 3-column: conversations list, thread, context panel.
// Roles:
//   - customer: conversation.buyer_id = user.id
//   - seller:   conversation.seller_id = seller.id
// Realtime: subscribes to messages INSERTs for the selected conversation.
export function Messages({
  user,
  seller,
  openSellerId,
  onOpenedSeller,
  openBuyerId,
  onOpenedBuyer,
  orderContext,
  onConsumedOrderContext,
  onOpenOrder,
}) {
  const queryClient = useQueryClient();
  const [selId, setSelId] = useState(null);

  const role = seller ? "seller" : "buyer";
  const scopeId = role === "buyer" ? user?.id : seller?.id;
  // Memoize queryKey so its reference is stable across renders. Without this,
  // the find-or-create effects below (deps include queryKey) would re-fire
  // on every render — racing themselves into a parallel INSERT that hits
  // the `unique_buyer_seller` unique constraint and logs a 409.
  const queryKey = useMemo(() => ["conversations", role, scopeId], [role, scopeId]);

  // Cached via TanStack Query — tab revisits return to the same conversation
  // list instantly. Realtime invalidates whenever a conversation row changes
  // (new message bumps last_message_at + preview).
  const { data: conversations = [], isLoading: loading } = useQuery({
    queryKey,
    enabled: !!scopeId,
    staleTime: 30_000,
    queryFn: async () => {
      let q = supabase
        .from("conversations")
        .select(
          "id, buyer_id, seller_id, last_message_at, last_message_preview, buyer_unread_count, seller_unread_count, created_at, buyer:users!conversations_buyer_id_fkey(name, avatar_url), seller:sellers(store_name, logo_url)"
        )
        .order("last_message_at", { ascending: false, nullsFirst: false })
        .limit(50);
      if (role === "buyer") q = q.eq("buyer_id", user.id);
      else if (seller?.id) q = q.eq("seller_id", seller.id);
      const { data } = await q;
      return data || [];
    },
  });

  // Buyer arriving from Orders/Store with an explicit seller — find or create
  // the conversation, then select it.
  //
  // We query Supabase directly by (buyer_id, seller_id) instead of searching
  // the cached top-50 list. The list misses conversations for heavy users
  // (>50 rows) and can't disambiguate duplicates (conversations has no unique
  // constraint on the pair, so old installs may have >1 row per pair). The
  // authoritative query orders by last_message_at DESC + limit(1), picking
  // the row with the real message history if duplicates exist — and creates
  // a new row only when there truly isn't one. Mirrors mobile's App.tsx flow.
  const CONV_SELECT =
    "id, buyer_id, seller_id, last_message_at, last_message_preview, buyer_unread_count, seller_unread_count, created_at, buyer:users!conversations_buyer_id_fkey(name, avatar_url), seller:sellers(store_name, logo_url)";

  useEffect(() => {
    if (role !== "buyer" || !openSellerId || !user?.id) return;
    let cancelled = false;
    (async () => {
      const { data: existing } = await supabase
        .from("conversations")
        .select(CONV_SELECT)
        .eq("buyer_id", user.id)
        .eq("seller_id", openSellerId)
        .order("last_message_at", { ascending: false, nullsFirst: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (existing) {
        queryClient.setQueryData(queryKey, (prev = []) => {
          if (prev.some((c) => c.id === existing.id)) return prev;
          return [existing, ...prev];
        });
        setSelId(existing.id);
        onOpenedSeller?.();
        return;
      }
      // upsert with onConflict makes this atomic: if the (buyer_id, seller_id)
      // pair already exists (because a parallel effect run, a realtime event,
      // or another tab beat us to it) we get the existing row back instead of
      // a 23505 unique_violation. The conversations table has a
      // `unique_buyer_seller` constraint backing this onConflict target.
      const { data: created, error: cErr } = await supabase
        .from("conversations")
        .upsert(
          { buyer_id: user.id, seller_id: openSellerId },
          { onConflict: "buyer_id,seller_id" }
        )
        .select(CONV_SELECT)
        .single();
      if (cancelled) return;
      // Only consume `openSellerId` on success. If we clear it after a failed
      // insert, the default-select effect below will immediately fall back to
      // `conversations[0]`, silently dumping the user into some other thread
      // — and if an `orderContext` is pending, injecting the order-ref card
      // into that wrong thread. Leaving `openSellerId` set keeps the thread
      // pane empty so the misrouting never happens.
      if (!cErr && created) {
        queryClient.setQueryData(queryKey, (prev = []) => [created, ...prev]);
        setSelId(created.id);
        onOpenedSeller?.();
      } else {
        console.error("[Messages] insert conversation failed (buyer flow)", cErr);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [openSellerId, role, user?.id, queryClient, onOpenedSeller, queryKey]);

  // Mirror image: seller arriving from an order drawer with an explicit buyer.
  useEffect(() => {
    if (role !== "seller" || !openBuyerId || !seller?.id) return;
    let cancelled = false;
    (async () => {
      const { data: existing } = await supabase
        .from("conversations")
        .select(CONV_SELECT)
        .eq("buyer_id", openBuyerId)
        .eq("seller_id", seller.id)
        .order("last_message_at", { ascending: false, nullsFirst: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (existing) {
        queryClient.setQueryData(queryKey, (prev = []) => {
          if (prev.some((c) => c.id === existing.id)) return prev;
          return [existing, ...prev];
        });
        setSelId(existing.id);
        onOpenedBuyer?.();
        return;
      }
      // Mirror of the buyer-flow upsert above — see that comment for why.
      const { data: created, error: cErr } = await supabase
        .from("conversations")
        .upsert(
          { buyer_id: openBuyerId, seller_id: seller.id },
          { onConflict: "buyer_id,seller_id" }
        )
        .select(CONV_SELECT)
        .single();
      if (cancelled) return;
      // Only consume `openBuyerId` on success. Previously we called
      // `onOpenedBuyer?.()` unconditionally, so when RLS silently blocked
      // the insert (pre-20260419000003 migration) the effect left no row,
      // cleared the signal, and the default-select effect below pointed the
      // user at `conversations[0]` — typically a different customer's chat,
      // where the pending `orderContext` then injected an order-ref card.
      // Leaving `openBuyerId` set on failure keeps the thread pane empty.
      if (!cErr && created) {
        queryClient.setQueryData(queryKey, (prev = []) => [created, ...prev]);
        setSelId(created.id);
        onOpenedBuyer?.();
      } else {
        console.error("[Messages] insert conversation failed (seller flow)", cErr);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [openBuyerId, role, seller?.id, queryClient, onOpenedBuyer, queryKey]);

  // Default selection to the most recent thread once the list is in.
  // Skip when an explicit open is pending — otherwise this effect races the
  // openBuyerId/openSellerId effects above and overwrites their setSelId
  // within the same render (both plain-value updates → last one wins). That
  // race caused the "Message customer" button to land on the wrong thread and
  // inject the order-ref card into some other buyer's conversation.
  //
  // Only fall back when `selId` has never been set. The previous
  // `!conversations.some(c => c.id === selId)` branch was meant to recover
  // from a deleted conversation, but it also fires during the brief window
  // between a successful insert's `setSelId(created.id)` and the TanStack
  // cache refetch that confirms the new row — sending the user back to
  // `conversations[0]` mid-flight. We'd rather leave a just-created
  // conversation selected (even if the cache hasn't caught up) than risk
  // re-introducing the wrong-chat fallback.
  useEffect(() => {
    if (!conversations.length) return;
    if (openSellerId || openBuyerId) return;
    if (!selId) {
      setSelId(conversations[0].id);
    }
  }, [conversations, selId, openSellerId, openBuyerId]);

  // Realtime: a new message bumps the conversation row — refetch the list so
  // the preview/unread counters stay live.
  useEffect(() => {
    if (!scopeId) return;
    const filter =
      role === "buyer" ? `buyer_id=eq.${user.id}` : `seller_id=eq.${seller.id}`;
    const channelName = `conversations:${role}:${scopeId}`;
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "conversations", filter },
        () => queryClient.invalidateQueries({ queryKey })
      )
      .subscribe(logChannelStatus(channelName));
    return () => {
      supabase.removeChannel(channel);
    };
  }, [scopeId, role, user?.id, seller?.id, queryClient, queryKey]);

  const sel = useMemo(() => conversations.find((c) => c.id === selId), [conversations, selId]);

  if (loading) {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "320px 1fr 280px", gap: 14, height: "calc(100vh - 120px)" }}>
        <Skeleton h={500} r={16} />
        <Skeleton h={500} r={16} />
        <Skeleton h={500} r={16} />
      </div>
    );
  }

  if (!conversations.length) {
    return (
      <div>
        <h1 style={{ fontSize: 28, fontWeight: 900, margin: "4px 0 18px", letterSpacing: "-0.6px" }}>Messages</h1>
        <EmptyState
          icon="message-square"
          title="No conversations yet"
          body={
            role === "buyer"
              ? "Start a conversation by messaging a seller from any store page."
              : "Once a buyer messages you, the conversation will appear here."
          }
        />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "320px 1fr 280px", gap: 14, height: "calc(100vh - 160px)" }}>
        <ConversationList conversations={conversations} selId={selId} setSelId={setSelId} role={role} />
        {sel ? (
          <Thread
            conversation={sel}
            userId={user.id}
            role={role}
            orderContext={orderContext}
            onConsumedOrderContext={onConsumedOrderContext}
            onOpenOrder={onOpenOrder}
          />
        ) : (
          <Card style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <EmptyState icon="message-square" title="Select a conversation" body="Pick a thread to start chatting." />
          </Card>
        )}
        {sel && <ContextPanel conversation={sel} role={role} />}
      </div>
    </div>
  );
}

function ConversationList({ conversations, selId, setSelId, role }) {
  return (
    <Card pad={0} style={{ overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <div
        style={{
          padding: "14px 16px",
          borderBottom: "1px solid rgba(196,193,183,0.25)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <h3 style={{ fontSize: 14, fontWeight: 900, margin: 0, letterSpacing: "-0.3px" }}>Messages</h3>
        <span style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>{conversations.length}</span>
      </div>
      <div style={{ overflowY: "auto", flex: 1 }}>
        {conversations.map((c) => {
          const on = c.id === selId;
          const title = role === "buyer" ? c.seller?.store_name || "Seller" : c.buyer?.name || "Buyer";
          const avatar = role === "buyer" ? c.seller?.logo_url : c.buyer?.avatar_url;
          const unread = role === "buyer" ? c.buyer_unread_count : c.seller_unread_count;
          const initial = title[0]?.toUpperCase() || "?";
          return (
            <button
              key={c.id}
              onClick={() => setSelId(c.id)}
              style={{
                all: "unset",
                display: "block",
                cursor: "pointer",
                width: "100%",
                padding: "12px 16px",
                borderBottom: "1px solid rgba(196,193,183,0.15)",
                background: on ? C.cream : "transparent",
              }}
            >
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    background: avatar ? `url(${avatar}) center/cover` : C.hunter,
                    color: C.dust,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                    fontWeight: 800,
                    flexShrink: 0,
                  }}
                >
                  {!avatar && initial}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: unread > 0 ? 900 : 700,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {title}
                  </div>
                  <div
                    style={{
                      fontSize: 11.5,
                      color: C.muted,
                      marginTop: 2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {(() => {
                      const raw = c.last_message_preview;
                      if (!raw) return "Start the conversation…";
                      const ref = decodeOrderRef(raw);
                      return ref ? orderRefPreview(ref) : raw;
                    })()}
                  </div>
                </div>
                {unread > 0 && (
                  <span
                    style={{
                      background: C.hunter,
                      color: C.dust,
                      fontSize: 10,
                      fontWeight: 800,
                      padding: "2px 7px",
                      borderRadius: 10,
                    }}
                  >
                    {unread}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

function Thread({ conversation, userId, role, orderContext, onConsumedOrderContext, onOpenOrder }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    supabase
      .from("messages")
      .select("id, conversation_id, sender_id, content, image_url, read_at, created_at")
      .eq("conversation_id", conversation.id)
      .order("created_at", { ascending: true })
      .limit(100)
      .then(async ({ data }) => {
        if (cancelled) return;
        const loaded = data || [];

        // Idempotent order-ref insert. The user clicked "Message" from an
        // order detail — drop a clickable card into the thread so both sides
        // know which order this is about. Skip if the same sub_order has
        // already been referenced in this thread (avoids spam on repeat
        // clicks).
        if (orderContext?.sub_order_id) {
          const already = loaded.some((m) => {
            const ref = decodeOrderRef(m.content);
            return ref?.sub_order_id === orderContext.sub_order_id;
          });
          if (!already) {
            const encoded = encodeOrderRef(orderContext);
            const { data: inserted } = await supabase
              .from("messages")
              .insert({
                conversation_id: conversation.id,
                sender_id: userId,
                content: encoded,
              })
              .select("id, conversation_id, sender_id, content, image_url, read_at, created_at")
              .single();
            // Bump the conversation preview so the list reflects this card.
            await supabase
              .from("conversations")
              .update({
                last_message_preview: orderRefPreview(orderContext),
                last_message_at: new Date().toISOString(),
              })
              .eq("id", conversation.id);
            if (inserted && !cancelled) loaded.push(inserted);
          }
          onConsumedOrderContext?.();
        }

        if (cancelled) return;
        setMessages(loaded);
        setLoading(false);
      });

    // Mark incoming messages as read and zero the per-role unread counter.
    const unreadCol = role === "buyer" ? "buyer_unread_count" : "seller_unread_count";
    supabase
      .from("messages")
      .update({ read_at: new Date().toISOString() })
      .eq("conversation_id", conversation.id)
      .neq("sender_id", userId)
      .is("read_at", null)
      .then(() => {});
    supabase
      .from("conversations")
      .update({ [unreadCol]: 0 })
      .eq("id", conversation.id)
      .then(() => {});

    const channelName = `messages:${conversation.id}`;
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages", filter: `conversation_id=eq.${conversation.id}` },
        (payload) => {
          setMessages((prev) => (prev.some((m) => m.id === payload.new.id) ? prev : [...prev, payload.new]));
        }
      )
      .subscribe(logChannelStatus(channelName));

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [conversation.id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  async function send() {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    const optimistic = {
      id: `local-${Date.now()}`,
      conversation_id: conversation.id,
      sender_id: userId,
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setDraft("");
    const { error } = await supabase
      .from("messages")
      .insert({ conversation_id: conversation.id, sender_id: userId, content: text });
    if (error) {
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setDraft(text);
    }
    setSending(false);
  }

  const otherName = role === "buyer" ? conversation.seller?.store_name : conversation.buyer?.name;
  const otherAvatar = role === "buyer" ? conversation.seller?.logo_url : conversation.buyer?.avatar_url;
  const initial = otherName?.[0]?.toUpperCase() || "?";

  return (
    <Card pad={0} style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div
        style={{
          padding: "14px 18px",
          borderBottom: "1px solid rgba(196,193,183,0.25)",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: otherAvatar ? `url(${otherAvatar}) center/cover` : C.hunter,
            color: C.dust,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
            fontWeight: 800,
          }}
        >
          {!otherAvatar && initial}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800 }}>{otherName || "Conversation"}</div>
          <div style={{ fontSize: 10.5, color: C.muted, fontWeight: 600 }}>Active on Kapio</div>
        </div>
      </div>

      <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: 18, background: C.bg }}>
        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <Skeleton h={40} w="60%" />
            <Skeleton h={40} w="50%" style={{ marginLeft: "auto" }} />
          </div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: "center", color: C.muted, fontSize: 12, marginTop: 40 }}>
            No messages yet. Start the conversation below.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {messages.map((m) => {
              const mine = m.sender_id === userId;
              const ref = decodeOrderRef(m.content);
              if (ref) {
                return (
                  <div key={m.id} style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start" }}>
                    <OrderRefCard
                      refData={ref}
                      mine={mine}
                      timestamp={m.created_at}
                      onOpen={() => onOpenOrder?.(ref.sub_order_id)}
                    />
                  </div>
                );
              }
              return (
                <div key={m.id} style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start" }}>
                  <div
                    style={{
                      maxWidth: "70%",
                      padding: "9px 13px",
                      borderRadius: 14,
                      background: mine ? C.hunter : C.white,
                      color: mine ? C.dust : C.charcoal,
                      fontSize: 13,
                      fontWeight: 500,
                      lineHeight: 1.45,
                      boxShadow: mine ? "none" : "0 1px 3px rgba(0,0,0,0.04)",
                      border: mine ? "none" : "1px solid rgba(196,193,183,0.3)",
                    }}
                  >
                    {m.content}
                    <div
                      style={{
                        fontSize: 9.5,
                        fontWeight: 600,
                        opacity: 0.6,
                        marginTop: 4,
                        textAlign: mine ? "right" : "left",
                      }}
                    >
                      {parseTs(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ padding: 12, borderTop: "1px solid rgba(196,193,183,0.25)", display: "flex", gap: 8 }}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
          placeholder="Write a message…"
          style={{
            flex: 1,
            border: "1px solid rgba(196,193,183,0.4)",
            borderRadius: 12,
            padding: "10px 14px",
            fontSize: 13,
            outline: "none",
            fontFamily: "inherit",
            background: C.white,
          }}
        />
        <button
          onClick={send}
          disabled={!draft.trim() || sending}
          style={{
            border: "none",
            borderRadius: 12,
            padding: "0 16px",
            background: C.hunter,
            color: C.dust,
            cursor: "pointer",
            fontWeight: 800,
            fontSize: 13,
            opacity: !draft.trim() || sending ? 0.5 : 1,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Icon name="send" size={14} color={C.dust} />
          Send
        </button>
      </div>
    </Card>
  );
}

function OrderRefCard({ refData, mine, timestamp, onOpen }) {
  const code = refData.sub_number || refData.parent_order_number || refData.sub_order_id?.slice(0, 8) || "Order";
  const status = refData.status ? refData.status.replaceAll("_", " ") : null;
  return (
    <button
      onClick={onOpen}
      style={{
        all: "unset",
        cursor: "pointer",
        maxWidth: 280,
        padding: 10,
        borderRadius: 14,
        background: mine ? C.hunter : C.white,
        color: mine ? C.dust : C.charcoal,
        border: mine ? "none" : "1px solid rgba(196,193,183,0.4)",
        boxShadow: mine ? "none" : "0 1px 3px rgba(0,0,0,0.04)",
        display: "block",
      }}
    >
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 10,
            background: refData.image
              ? `url(${refData.image}) center/cover`
              : mine
              ? "rgba(255,255,255,0.15)"
              : C.cream,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 22,
            flexShrink: 0,
          }}
        >
          {!refData.image && "📦"}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 9.5,
              fontWeight: 800,
              letterSpacing: 0.8,
              opacity: 0.7,
              textTransform: "uppercase",
            }}
          >
            Order
          </div>
          <div className="mono" style={{ fontSize: 13, fontWeight: 900, marginTop: 1 }}>
            {code}
          </div>
          {status && (
            <div style={{ fontSize: 10.5, fontWeight: 700, opacity: 0.75, marginTop: 2, textTransform: "capitalize" }}>
              {status}
            </div>
          )}
        </div>
        <Icon name="chevron-right" size={16} color={mine ? C.dust : C.muted} />
      </div>
      <div
        style={{
          fontSize: 9.5,
          fontWeight: 600,
          opacity: 0.6,
          marginTop: 6,
          textAlign: mine ? "right" : "left",
        }}
      >
        {parseTs(timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
      </div>
    </button>
  );
}

function ContextPanel({ conversation, role }) {
  const title = role === "buyer" ? conversation.seller?.store_name : conversation.buyer?.name;
  return (
    <Card>
      <h3 style={{ fontSize: 13, fontWeight: 900, margin: "0 0 12px", letterSpacing: "-0.2px" }}>Context</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <InfoRow label={role === "buyer" ? "Seller" : "Buyer"} value={title || "—"} />
        <InfoRow
          label="Last message"
          value={
            conversation.last_message_at
              ? parseTs(conversation.last_message_at).toLocaleString()
              : "—"
          }
        />
        <InfoRow label="Started" value={parseTs(conversation.created_at).toLocaleDateString()} />
      </div>
    </Card>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>
        {label}
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 700, marginTop: 2, wordBreak: "break-word" }}>{value}</div>
    </div>
  );
}
